import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useShowOnActivity } from "@air/hook-use-show-on-activity";
import { Button } from "@air/primitive-button";
import { Spinner } from "@air/primitive-spinner";
import isNil from "lodash/isNil";
import dynamic from "next/dynamic";
import { memo, useCallback, useEffect, useMemo, useRef } from "react";
import { useSelector } from "react-redux";
import { getPageNumber } from "~/components/Annotations/shared/annotationUtils";
import { PDFPageAnnotations } from "~/components/AssetModal/Visualizer/PDFVisualizer/components/PDFPageAnnotations";
import { usePDFAssetLinkWithData } from "~/components/AssetModal/Visualizer/PDFVisualizer/hooks";
import { useTogglePDFFullscreen } from "~/components/AssetModal/Visualizer/PDFVisualizer/hooks/useTogglePDFFullscreen";
import { QueryParamNames } from "~/constants/search";
import { useAnnotationContextSelector } from "~/providers/AnnotationProvider/AnnotationProvider";
import { activeAnnotationSelector } from "~/providers/AnnotationProvider/annotationProviderUtils";
import { setCentralizedClipPDFNumberOfPagesAction } from "~/store/centralizedClip/actions";
import { centralizedClipIdSelector } from "~/store/centralizedClip/selectors";
import { reportErrorToBugsnag } from "~/utils/ErrorUtils";
import { useAirStore } from "~/utils/ReduxUtils";
const PDFViewer = dynamic(()=>import("@air/react-pdfjs-viewer").then((mod)=>mod.PDFViewer).catch((e)=>{
        console.error("ERROR", e);
        // eslint-disable-next-line react/display-name
        return ()=>/*#__PURE__*/ _jsx("div", {
                className: "flex size-full items-center justify-center text-white",
                children: "Something went wrong"
            });
    }), {
    loadableGenerated: {
        webpack: ()=>[
                require.resolveWeak("@air/react-pdfjs-viewer")
            ]
    },
    ssr: false,
    loading: ()=>/*#__PURE__*/ _jsx("div", {
            className: "flex size-full items-center justify-center",
            children: /*#__PURE__*/ _jsx(Spinner, {
                className: "size-8 text-teal-9"
            })
        })
});
const PDFControlsWrapper = dynamic(()=>import("~/components/AssetModal/Visualizer/PDFVisualizer/PDFControls/PDFControlsWrapper").then((mod)=>mod.PDFControlsWrapper), {
    loadableGenerated: {
        webpack: ()=>[
                require.resolveWeak("~/components/AssetModal/Visualizer/PDFVisualizer/PDFControls/PDFControlsWrapper")
            ]
    },
    ssr: false
});
const PDFSearchWrapper = dynamic(()=>import("~/components/AssetModal/Visualizer/PDFVisualizer/PDFSearch/PDFSearchWrapper").then((mod)=>mod.PDFSearchWrapper), {
    loadableGenerated: {
        webpack: ()=>[
                require.resolveWeak("~/components/AssetModal/Visualizer/PDFVisualizer/PDFSearch/PDFSearchWrapper")
            ]
    },
    ssr: false
});
export const PDFVisualizer = /*#__PURE__*/ memo((param)=>{
    let { renderOutlinePageMenu, renderControlsMenu, canAnnotateClip, insertSearchFromQuery, getFallbackPDFUrl } = param;
    const clipId = useSelector(centralizedClipIdSelector);
    const controlsRef = useRef(null);
    const containerRef = useRef(null);
    const pdfViewerRef = useRef(null);
    const { dispatch } = useAirStore();
    const { data: pdfData, refetchPdfAsset, hasError } = usePDFAssetLinkWithData({
        getFallbackUrl: getFallbackPDFUrl
    });
    const activeAnnotation = useAnnotationContextSelector(activeAnnotationSelector);
    const { isFullScreen, onToggleFullscreen } = useTogglePDFFullscreen(containerRef);
    const shouldHideControlsRef = useRef(true);
    const getShouldHideControls = useCallback(()=>shouldHideControlsRef.current, []);
    const setShouldEnableHide = useCallback((value)=>{
        shouldHideControlsRef.current = value;
    }, []);
    useShowOnActivity({
        containerRef,
        displayRef: controlsRef,
        getShouldHide: getShouldHideControls
    });
    const renderPageAnnotations = useCallback((param)=>{
        let { pageNumber, containerRef } = param;
        return /*#__PURE__*/ _jsx(PDFPageAnnotations, {
            pageNumber: pageNumber,
            canAnnotate: !isFullScreen && canAnnotateClip,
            containerRef: containerRef
        });
    }, [
        canAnnotateClip,
        isFullScreen
    ]);
    useEffect(()=>{
        if (activeAnnotation) {
            const pageNumber = getPageNumber(activeAnnotation);
            if (!isNil(pageNumber)) {
                var _pdfViewerRef_current;
                (_pdfViewerRef_current = pdfViewerRef.current) === null || _pdfViewerRef_current === void 0 ? void 0 : _pdfViewerRef_current.scrollToPage(pageNumber);
            }
        }
    }, [
        activeAnnotation
    ]);
    const onPDFLoaded = useCallback((pdfDocument)=>{
        const searchParams = new URLSearchParams(window.location.search);
        const pdfPageParam = searchParams.get(QueryParamNames.page);
        const page = pdfPageParam ? parseInt(pdfPageParam, 10) : null;
        dispatch(setCentralizedClipPDFNumberOfPagesAction({
            clipId,
            pdfPageNumber: pdfDocument.numPages
        }));
        if (page) {
            var _pdfViewerRef_current;
            (_pdfViewerRef_current = pdfViewerRef.current) === null || _pdfViewerRef_current === void 0 ? void 0 : _pdfViewerRef_current.scrollToPage(page);
        }
    }, [
        clipId,
        dispatch
    ]);
    const onPDFViewerError = useCallback((param)=>{
        let { error, context, metadata } = param;
        reportErrorToBugsnag({
            error,
            context,
            metadata
        });
    }, []);
    const errorView = useMemo(()=>hasError ? /*#__PURE__*/ _jsxs("div", {
            className: "flex flex-col items-center gap-4 text-center text-gray-100",
            children: [
                /*#__PURE__*/ _jsx("div", {
                    children: "We're having trouble loading this PDF. Please check your connection and try again."
                }),
                /*#__PURE__*/ _jsx(Button, {
                    onClick: refetchPdfAsset,
                    appearance: "outline",
                    children: "Reload"
                })
            ]
        }) : null, [
        hasError,
        refetchPdfAsset
    ]);
    return /*#__PURE__*/ _jsx("div", {
        "data-testid": "PDF_VIEWER",
        // 180px is enough space to show header and bar with icons in the bottom
        className: "flex size-full max-h-[calc(100vh-180px)] items-center justify-center md:max-h-screen",
        ref: containerRef,
        children: /*#__PURE__*/ _jsx(PDFViewer, {
            onError: onPDFViewerError,
            handleRef: pdfViewerRef,
            pdfData: pdfData,
            renderOutlinePageMenu: renderOutlinePageMenu,
            renderPageLayers: renderPageAnnotations,
            onPDFLoaded: onPDFLoaded,
            errorView: errorView,
            children: /*#__PURE__*/ _jsxs(_Fragment, {
                children: [
                    /*#__PURE__*/ _jsx("div", {
                        className: "absolute right-5 top-5 overflow-hidden",
                        children: /*#__PURE__*/ _jsx(PDFSearchWrapper, {
                            insertSearchFromQuery: insertSearchFromQuery
                        })
                    }),
                    /*#__PURE__*/ _jsx("div", {
                        className: "absolute bottom-4 w-full",
                        children: /*#__PURE__*/ _jsx(PDFControlsWrapper, {
                            forwardedRef: controlsRef,
                            renderControlsMenu: renderControlsMenu,
                            setShouldEnableHide: setShouldEnableHide,
                            isFullScreen: isFullScreen,
                            onToggleFullscreen: onToggleFullscreen
                        })
                    })
                ]
            })
        })
    });
});
PDFVisualizer.displayName = "PDFVisualizer";
